import '@wix/ricos/css/plugin-giphy-viewer.global.css';
import '@wix/ricos/css/plugin-image-viewer.global.css';
import '@wix/ricos/css/plugin-link-viewer.global.css';
import '@wix/ricos/css/plugin-mentions-viewer.global.css';
import '@wix/ricos/css/plugin-video-viewer.global.css';
import React from 'react';
import classNames from 'classnames';
import { flowRight } from 'lodash';
import memoizeOne from 'memoize-one';
import PropTypes from 'prop-types';
import { TPAComponentsProvider } from 'wix-ui-tpa/cssVars';
import { WixComments, WixCommentsApiProvider } from '@wix/comments-ooi-client';
import { WixCommentsTranslationsProvider } from '@wix/comments-ooi-translations-provider';
import {
  EMOJI_TYPE,
  GIPHY_TYPE,
  IMAGE_TYPE,
  LINK_TYPE,
  MENTION_TYPE,
  pluginEmoji,
  pluginEmojiViewer,
  pluginGiphy,
  pluginGiphyViewer,
  pluginImage,
  pluginImageViewer,
  pluginLink,
  pluginLinkViewer,
  pluginMentions,
  pluginMentionsViewer,
  pluginTextColor,
  pluginTextColorViewer,
  pluginVideo,
  pluginVideoViewer,
  TEXT_COLOR_TYPE,
  VIDEO_TYPE,
} from '@wix/ricos';
import { useExperiments } from '@wix/yoshi-flow-editor';
import {
  formatDate,
  getTPASettingsIsMandatoryRatingsEnabled,
  getTPASettingsIsRatingsEnabled,
  resolveLegacyId,
  urijs,
} from '@wix/communities-blog-client-common';
import { EXPERIMENT_WIX_COMMENTS_USE_REACTIONS } from '@wix/communities-blog-experiments';
import { RootPropsContext } from '../../../common/components/root-props/root-props-context';
import { connect } from '../../../common/components/runtime-context';
import withDeviceType from '../../../common/hoc/with-device-type';
import withFontClassName from '../../../common/hoc/with-font-class-name';
import { usePostBorderWidth } from '../../../common/hooks/use-post-border-width';
import { isRtlLanguage } from '../../../common/services/is-rtl-language';
import {
  getCommentId,
  getLanguage,
  getUrl,
  isSite,
} from '../../../common/store/basic-params/basic-params-selectors';
import styles from './post-page-wix-comments.scss';

const PostPageWixComments = ({
  post,
  language,
  lng,
  isMobile,
  isSledRun,
  commentId,
  isSite,
  fetchMentions,
  isRatingsEnabled,
  isRatingsMandatory,
}) => {
  const { borderWidth } = usePostBorderWidth();
  const { experiments } = useExperiments();

  const isWixCommentsUseReactionsEnabled = experiments.enabled(
    EXPERIMENT_WIX_COMMENTS_USE_REACTIONS,
  );

  const getPluginConfig = memoizeOne((fetchMentions) => {
    return {
      [MENTION_TYPE]: {
        editorModule: pluginMentions,
        viewerModule: pluginMentionsViewer,
        config: {
          getMentions: fetchMentions,
          supportWhitespace: false,
        },
      },
      [LINK_TYPE]: { editorModule: pluginLink, viewerModule: pluginLinkViewer },
      [EMOJI_TYPE]: {
        editorModule: pluginEmoji,
        viewerModule: pluginEmojiViewer,
      },
      [IMAGE_TYPE]: {
        editorModule: pluginImage,
        viewerModule: pluginImageViewer,
      },
      [GIPHY_TYPE]: {
        editorModule: pluginGiphy,
        viewerModule: pluginGiphyViewer,
      },
      [VIDEO_TYPE]: {
        editorModule: pluginVideo,
        viewerModule: pluginVideoViewer,
      },
      [TEXT_COLOR_TYPE]: {
        editorModule: pluginTextColor,
        viewerModule: pluginTextColorViewer,
      },
    };
  });

  const getTpaProviderProps = memoizeOne((isMobile, lng) => ({
    mobile: isMobile,
    rtl: isRtlLanguage(lng),
  }));

  const getDeepLinkProp = memoizeOne((commentId, isSite) => ({
    commentId,
    generateLink: (commentId) => {
      if (isSite) {
        const url = urijs(window.location.href);
        url.setQuery({ commentId });

        return url.toString();
      }
    },
  }));

  const getCtxFields = memoizeOne((postId) => ({
    contextId: postId,
    contextType: 'postId',
  }));

  const getReactionsConfig = memoizeOne((isWixCommentsUseReactionsEnabled) =>
    isWixCommentsUseReactionsEnabled
      ? {
          commentReactions: { type: 'emotions' },
          replyReactions: { type: 'emotions' },
        }
      : {},
  );

  const getRatingsConfig = memoizeOne(
    ({ isRatingsEnabled, isRatingsMandatory }) =>
      isRatingsEnabled ? { isRequired: isRatingsMandatory } : undefined,
  );

  const formatRelativeTime = (time) =>
    formatDate({ date: time, lng: language });

  return (
    <div
      className={classNames(
        styles.container,
        'blog-card-border-color',
        'blog-card-background-color',
      )}
      style={{ '--wcBorderWidth': `${borderWidth}px`, borderWidth }}
    >
      <div className={styles.innerContainer}>
        <RootPropsContext.Consumer>
          {(props) => {
            return (
              <WixCommentsApiProvider {...props}>
                <WixCommentsTranslationsProvider>
                  <TPAComponentsProvider
                    value={getTpaProviderProps(isMobile, lng)}
                  >
                    <WixComments
                      hideZeroCommentsEmptyState
                      screenshotMode={isSledRun}
                      pluginConfig={getPluginConfig(fetchMentions)}
                      resourceId={resolveLegacyId(post)}
                      deepLink={getDeepLinkProp(commentId, isSite)}
                      ctxFields={getCtxFields(resolveLegacyId(post))}
                      pagination={PAGINATION_CONFIG}
                      formatRelativeTime={formatRelativeTime}
                      isLocked={post.isCommentsDisabled}
                      reactions={getReactionsConfig(
                        isWixCommentsUseReactionsEnabled,
                      )}
                      a11yOptions={ALLY_OPTIONS}
                      contentTruncationLimit={isMobile ? 80 : 120}
                      ratings={getRatingsConfig({
                        isRatingsEnabled,
                        isRatingsMandatory,
                      })}
                      useRicosNext
                    />
                  </TPAComponentsProvider>
                </WixCommentsTranslationsProvider>
              </WixCommentsApiProvider>
            );
          }}
        </RootPropsContext.Consumer>
      </div>
    </div>
  );
};

const ALLY_OPTIONS = { headerRank: 'h2' };

const PAGINATION_CONFIG = {
  replyShowMoreLimit: 8,
  initialPage: {
    commentLimit: 20,
    replyLimit: 0,
  },
  pagination: {
    commentLimit: 20,
    replyLimit: 0,
  },
  maxPagesBeforeDrop: {
    commentPages: 2,
    replyPages: 4,
  },
};

PostPageWixComments.propTypes = {
  post: PropTypes.object,
  contentFontClassName: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  isSledRun: PropTypes.bool,
  lng: PropTypes.string,
  fetchMentions: PropTypes.func.isRequired,
  commentId: PropTypes.string,
  getCommentDeepLink: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  isRatingsEnabled: PropTypes.bool.isRequired,
  isRatingsMandatory: PropTypes.bool.isRequired,
};

const mapRuntimeToProps = (state, _, actions) => {
  return {
    isSledRun: getUrl(state).includes('viewerPlatformOverrides'),
    fetchMentions: actions.fetchMentionsPromisified,
    commentId: getCommentId(state),
    isSite: isSite(state),
    language: getLanguage(state),
    isRatingsEnabled: getTPASettingsIsRatingsEnabled(state),
    isRatingsMandatory: getTPASettingsIsMandatoryRatingsEnabled(state),
  };
};

export default flowRight(
  connect(mapRuntimeToProps),
  withDeviceType,
  withFontClassName,
)(PostPageWixComments);
